import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveLine } from '@nivo/line';
import { Fetch } from '../../utils/Utils.js';
import { formatDate } from '../../../utils/dateFormat.js';
import './NivoChartCard.css';

// Fixed categorical hue order (blue, orange, aqua, yellow, ...) — CVD-safety
// mechanism is the ORDER, never reassigned/cycled per render. Only the first
// four slots are used here since a ranch realistically runs a handful of
// concurrent reproduction lots, not eight.
const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];

// Format "2026-01" to "Jan 2026" (or "fev. de 2026" in PT)
const formatMonthLabel = (yearMonth) => {
  const [year, month] = yearMonth.split('-');
  const date = new Date(year, parseInt(month) - 1);
  return formatDate(date, { month: 'short', year: 'numeric' });
};

const ReproductionSuccessByMonthAndGroup = () => {
  const { t } = useTranslation();
  const [series, setSeries] = useState(null);
  const [legendItems, setLegendItems] = useState([]);
  const [summary, setSummary] = useState('');

  useEffect(() => {
    let results = null;
    let lots = null;

    const tryBuild = () => {
      if (!results || !lots) return;
      build(results, lots);
    };

    Fetch('/api/reproduction/results', 'GET', null, (data) => { results = data || []; tryBuild(); });
    Fetch('api/data/lots?search_fields=type&search_values=reproduction', 'get', null, (data) => { lots = data || []; tryBuild(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const build = (results, lots) => {
    const lotNameById = {};
    lots.forEach(l => { lotNameById[l.lot_id] = l.name; });

    // Each line is one reproduction lot — its REPRO-code is what WhatsApp
    // uses to address it, so that stays, but the code alone doesn't say
    // which pasture/group it is; pair it with the lot's own name when it has
    // one.
    const labelFor = (lotId) => {
      const code = `${t('lot_prefix')}-${lotId.toString().padStart(4, '0')}`;
      const name = lotNameById[lotId];
      return name ? `${name} (${code})` : code;
    };

    const agg = {};
    const months = [];
    const lotIds = [];

    results.forEach(res => {
      const month = (res.start_date || '').substring(0, 7);
      if (!month) return;
      // A dam dying isn't the lot's fault — excluded from both the success
      // count and the total, not counted as a failure.
      if (res.outcome === 'dam_died') return;

      if (!agg[month]) agg[month] = {};
      if (!agg[month][res.lot_id]) agg[month][res.lot_id] = { total: 0, success: 0 };
      agg[month][res.lot_id].total++;
      // A stillbirth still proves the pregnancy carried to term — treat both
      // calving outcomes as success, matching every other reproduction chart.
      if (res.outcome === 'calved_alive' || res.outcome === 'calved_stillborn') agg[month][res.lot_id].success++;

      if (!months.includes(month)) months.push(month);
      if (!lotIds.includes(res.lot_id)) lotIds.push(res.lot_id);
    });

    months.sort();

    if (lotIds.length === 0) {
      setSummary(t('chart_summary_no_repro_data'));
      setSeries(null);
      return;
    }

    // Gaps (null) for months a lot ran no cycle at all — a lot that's simply
    // idle that month isn't "0% success," it's "no data," and drawing a line
    // down to zero between real data points would misstate that.
    const built = lotIds.map((lotId, i) => ({
      id: labelFor(lotId),
      color: SERIES_COLORS[i % SERIES_COLORS.length],
      data: months.map(month => {
        const cell = agg[month]?.[lotId];
        return { x: month, y: cell ? Math.round((cell.success / cell.total) * 100) : null };
      }),
    }));

    let bestLabel = '', bestRate = -1;
    lotIds.forEach(lotId => {
      months.forEach(month => {
        const cell = agg[month]?.[lotId];
        if (!cell) return;
        const pct = Math.round((cell.success / cell.total) * 100);
        if (pct > bestRate) { bestRate = pct; bestLabel = labelFor(lotId); }
      });
    });
    setSummary(bestLabel ? t('chart_summary_best_lot', { lot: bestLabel, percent: bestRate }) : t('chart_summary_no_repro_data'));
    setSeries(built);
    setLegendItems(built.map(s => ({ id: s.id, color: s.color })));
  };

  return (
    <div className="Chart NivoCard">
      <h3>{t('pregnancy-success-rate-by-lot')}</h3>
      <p className="nivo-card-subtitle">{summary}</p>

      {!series ? (
        <p className="nivo-card-empty">{t('chart_summary_no_repro_data')}</p>
      ) : (
        <>
          {/* Hand-built legend, not Nivo's in-plot one — keeps identity
              fully separate from the drawing area instead of crowding it. */}
          <ul className="nivo-legend-row">
            {legendItems.map(item => (
              <li key={item.id}>
                <span className="nivo-legend-dot" style={{ background: item.color }} />
                {item.id}
              </li>
            ))}
          </ul>
          <div style={{ height: 340 }}>
            <ResponsiveLine
              data={series}
              margin={{ top: 16, right: 30, bottom: 50, left: 64 }}
              xScale={{ type: 'point' }}
              yScale={{ type: 'linear', min: 0, max: 100 }}
              axisLeft={{ legend: t('birth-rate-percentage'), legendOffset: -52, legendPosition: 'middle', tickValues: 5, format: (v) => `${v}%` }}
              axisBottom={{ format: formatMonthLabel, tickRotation: 0 }}
              colors={{ datum: 'color' }}
              lineWidth={2}
              pointSize={8}
              useMesh={true}
              enableGridX={false}
              gridYValues={5}
              motionConfig="gentle"
              theme={{
                axis: { ticks: { text: { fill: '#898781', fontSize: 12 } }, legend: { text: { fill: '#52514e', fontSize: 12.5 } } },
                grid: { line: { stroke: '#e1e0d9', strokeWidth: 1 } },
              }}
              tooltip={({ point }) => (
                <div className="nivo-tooltip">
                  <span className="nivo-tooltip-swatch" style={{ background: point.seriesColor }} />
                  <strong>{point.seriesId}</strong>: <span className="nivo-tooltip-value">{point.data.y}%</span> ({formatMonthLabel(point.data.x)})
                </div>
              )}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ReproductionSuccessByMonthAndGroup;
